import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ParcImmobilierMinimalDto, RealEstateDto } from 'api/real-estate';
import { realEstateAPI } from 'services/swagger-api/realEstateAPI';
import { RootState } from 'app/store';
import { AbpPolicies } from 'core/constants/AbpPolicies';

const PAGE_SIZE = 10;

export interface UsePersonBiensResult {
  /** Biens de la page courante, pour la recherche courante. */
  biens: RealEstateDto[];
  /** Total serveur pour la recherche courante. */
  totalCount: number;
  /** Total serveur SANS recherche — stable, pilote la visibilité de l'onglet et le badge. */
  initialTotal: number | null;
  /** Parcs immobiliers appartenant au client. */
  parcs: ParcImmobilierMinimalDto[];
  loading: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (value: string) => void;
  error: string | null;
}

/**
 * Données du parc immobilier d'un client : biens en pagination serveur
 * (recherche côté serveur, même design de liste que le tunnel de souscription)
 * + parcs du client. Chargé au niveau de PersonContent : pilote la visibilité
 * de l'onglet "Biens immobiliers" (masqué si aucun bien ni parc) et alimente
 * son contenu.
 *
 * Gating par policy ABP : les rôles sans BienImmobilier.Read (ex: Plateforme,
 * deny-list volontaire) ne déclenchent AUCUN appel — onglet masqué proprement,
 * sans 403 ni bruit serveur.
 */
export const usePersonBiens = (personId: string): UsePersonBiensResult => {
  // grantedPolicies = permissions ACCORDÉES à l'utilisateur (auth.policies liste
  // toutes les permissions définies, true pour tout le monde — inutilisable en gating).
  const policies = useSelector(
    (state: RootState) => state.abp.configuration?.auth?.grantedPolicies ?? {}
  );
  const canReadBiens = policies[AbpPolicies.BIEN_IMMO_READ] === true;
  const canReadParcs = policies[AbpPolicies.PARC_IMMO_DEFAULT] === true;

  const [biens, setBiens] = useState<RealEstateDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [initialTotal, setInitialTotal] = useState<number | null>(null);
  const [parcs, setParcs] = useState<ParcImmobilierMinimalDto[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(canReadBiens);
  const [error, setError] = useState<string | null>(null);
  // Garde anti-obsolescence : chaque changement de critère invalide les réponses en vol
  // (une réponse de l'ancienne recherche ne doit pas écraser la nouvelle page).
  const fetchSeqRef = useRef(0);

  // Debounce de la recherche (serveur)
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Retour page 1 quand la recherche change.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Parcs du client : GET /parcs-immobiliers est scopé au périmètre du courtier → filtre par ownerId.
  // Facultatif : un échec n'empêche pas l'affichage des biens.
  useEffect(() => {
    if (!canReadParcs) return;
    let cancelled = false;
    realEstateAPI
      .getListParcs()
      .then(response => {
        if (!cancelled) {
          setParcs(
            (response.realEstateParks ?? []).filter(parc => parc.ownerId === personId)
          );
        }
      })
      .catch(err => {
        console.error('Erreur lors de la récupération des parcs du client:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [personId, canReadParcs]);

  const fetchPage = useCallback(
    async (pageNumber: number, seq: number) => {
      setLoading(true);
      setError(null);
      try {
        const response = await realEstateAPI.searchBiensByProprietaire({
          personneId: personId,
          getGenericListCommandDto: {
            pageNumber,
            pageSize: PAGE_SIZE,
            searchString: debouncedSearch || undefined
          }
        });

        // Réponse obsolète (les critères ont changé depuis l'envoi) : ignorer.
        if (seq !== fetchSeqRef.current) return;

        const total = response.totalCount ?? 0;
        setBiens(response.items ?? []);
        setTotalCount(total);
        // Le total sans recherche est la référence stable (onglet, badge, bandeau stats).
        if (!debouncedSearch) setInitialTotal(total);
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        console.error('Erreur lors de la récupération du parc immobilier:', err);
        setBiens([]);
        setError('Impossible de charger le parc immobilier.');
      } finally {
        if (seq === fetchSeqRef.current) setLoading(false);
      }
    },
    [personId, debouncedSearch]
  );

  // (Re)chargement à chaque changement de critère ou de page.
  useEffect(() => {
    if (!canReadBiens) {
      setLoading(false);
      setInitialTotal(0);
      return;
    }
    fetchSeqRef.current += 1;
    fetchPage(page, fetchSeqRef.current);
  }, [fetchPage, page, canReadBiens]);

  return useMemo(
    () => ({
      biens,
      totalCount,
      initialTotal,
      parcs,
      loading,
      page,
      pageSize: PAGE_SIZE,
      setPage,
      search,
      setSearch,
      error
    }),
    [biens, totalCount, initialTotal, parcs, loading, page, search, error]
  );
};
